// The tap state machine. Pure functions, no storage and no React, so the one
// rule that is easy to get wrong can be tested directly.
//
// THE RULE, from Luke's guide and verified against the pilot data:
//
//   - Tapping a DIFFERENT category closes the open block and opens a new one.
//   - Tapping the SAME category, when it is one of the three counter
//     categories, increments that block's table count and does NOT close it.
//   - Tapping the same category when it is not a counter category does
//     nothing. Re-tapping "idle" does not mean a second idle.
//   - The OPENING tap counts as one table. 838 seconds over 105 taps is
//     8.0 sec/instance, which only reconciles if it does.
//
// Tap counts and block counts are never interchangeable: one continuous block
// can contain many taps.

import { BY_ID, type CategoryId } from "./categories.ts";
import { durationMilliseconds } from "./duration.ts";

export interface Block {
  id: string;                 // client-generated, so replay is idempotent
  categoryId: CategoryId;
  startedAt: number;          // epoch ms
  endedAt: number | null;
  seq: number;
  taps: Tap[];
}

export interface Tap {
  id: string;
  tappedAt: number;
}

export interface TrackerState {
  blocks: Block[];
  nextSeq: number;
}

export type TapOutcome =
  | { kind: "opened"; block: Block; closed: Block | null }
  | { kind: "counted"; block: Block; tap: Tap }
  | { kind: "ignored"; reason: string };

export const emptyState = (): TrackerState => ({ blocks: [], nextSeq: 1 });

export const openBlock = (s: TrackerState): Block | null => {
  const last = s.blocks[s.blocks.length - 1];
  return last && last.endedAt === null ? last : null;
};

/**
 * Apply one tap. Returns the new state and what happened, so the caller can
 * persist exactly the rows that changed rather than rewriting the session.
 */
export function tap(
  state: TrackerState,
  categoryId: CategoryId,
  at: number,
  newId: () => string,
): { state: TrackerState; outcome: TapOutcome } {
  const cat = BY_ID.get(categoryId);
  if (!cat) return { state, outcome: { kind: "ignored", reason: "unknown category" } };

  const current = openBlock(state);
  // A phone clock can step backwards between two taps (a network time fix, a
  // manual change). Without this, the closed block's ended_at lands before its
  // started_at, the database refuses the row, and before sync_batch learned to
  // reject that row on its own it failed every batch after it for the rest of
  // the session. Time never runs backwards inside one session.
  const t = current ? Math.max(at, current.startedAt) : at;

  // Same category as the open block.
  if (current && current.categoryId === categoryId) {
    if (!cat.countsTables) {
      // Re-tapping a non-counter category is a no-op, not a new block. Without
      // this, a nervous double-tap on "idle" would split one idle period into
      // two and shorten neither.
      return { state, outcome: { kind: "ignored", reason: "already in this category" } };
    }
    const counted: Tap = { id: newId(), tappedAt: t };
    const updated: Block = { ...current, taps: [...current.taps, counted] };
    return {
      state: { ...state, blocks: [...state.blocks.slice(0, -1), updated] },
      outcome: { kind: "counted", block: updated, tap: counted },
    };
  }

  // Different category, or nothing open: close what is open and start a block.
  const closed: Block | null = current ? { ...current, endedAt: t } : null;
  const fresh: Block = {
    id: newId(),
    categoryId,
    startedAt: t,
    endedAt: null,
    seq: state.nextSeq,
    // The opening tap counts as one table for counter categories. This single
    // line is the difference between every table count being correct and every
    // one being short by one per block.
    taps: cat.countsTables ? [{ id: newId(), tappedAt: t }] : [],
  };
  const blocks = closed ? [...state.blocks.slice(0, -1), closed, fresh] : [...state.blocks, fresh];
  return {
    state: { blocks, nextSeq: state.nextSeq + 1 },
    outcome: { kind: "opened", block: fresh, closed },
  };
}

/** Close the session's final open block. */
export function endSession(state: TrackerState, at: number): { state: TrackerState; closed: Block | null } {
  const current = openBlock(state);
  if (!current) return { state, closed: null };
  const closed: Block = { ...current, endedAt: Math.max(at, current.startedAt) };
  return { state: { ...state, blocks: [...state.blocks.slice(0, -1), closed] }, closed };
}

/**
 * Rebuilds tracker state for a session reopened after a reload or a discarded
 * tab. Only blocks still in the queue come back, because confirmed ones have
 * left it; that is enough to carry on, since the open block is never confirmed.
 * The block number must never be reused, or every later batch fails.
 */
export function resumeState(blocks: Block[], nextSeq?: number): TrackerState {
  const sorted = [...blocks].sort((a, b) => a.seq - b.seq);
  const highest = sorted.reduce((m, b) => Math.max(m, b.seq), 0);
  return { blocks: sorted, nextSeq: Math.max(nextSeq ?? 1, highest + 1) };
}

/** What the end-of-session summary shows. Seconds and taps, never conflated. */
export function summarise(state: TrackerState, now: number) {
  const byCat = new Map<CategoryId, { milliseconds: number; taps: number; blocks: number }>();
  for (const b of state.blocks) {
    const end = b.endedAt ?? now;
    const row = byCat.get(b.categoryId) ?? { milliseconds: 0, taps: 0, blocks: 0 };
    row.milliseconds += durationMilliseconds(b.startedAt, end);
    row.taps += b.taps.length;
    row.blocks += 1;
    byCat.set(b.categoryId, row);
  }
  return new Map([...byCat].map(([id,{milliseconds,...row}])=>[id,{...row,seconds:milliseconds/1000}]));
}
